import { useState } from "react";
import { motion, useMotionValueEvent, useTransform } from "framer-motion";
import { ABOUT_STORY } from "../../data/aboutData";
import SceneNextLink from "./SceneNextLink";

const chapterFor = progress => progress < 0.3 ? -1 : progress < 0.52 ? 0 : progress < 0.72 ? 1 : 2;
const CHAPTER_STOPS = [0.41, 0.62, 0.86];

function AboutChapter({ point, index, progress, active }) {
  const start = [0.3, 0.52, 0.72][index];
  const end = [0.52, 0.72, 1][index];
  const opacity = useTransform(progress, index === 2 ? [start, start + 0.055, 1] : [start, start + 0.055, end - 0.035, end], index === 2 ? [0, 1, 1] : [0, 1, 1, 0]);
  const y = useTransform(progress, index === 2 ? [start, start + 0.055, 1] : [start, start + 0.055, end - 0.035, end], index === 2 ? [32, 0, 0] : [32, 0, 0, -24]);
  return (
    <motion.article
      data-about-story={index}
      data-active={active}
      aria-hidden={!active}
      inert={!active}
      className="absolute inset-x-0 top-0 border-l border-brand-500/40 pl-6"
      style={{ opacity, y }}
    >
      <div className="mb-3 flex items-center gap-3">
        <point.icon aria-hidden="true" className="h-5 w-5 shrink-0 text-brand-400" />
        <h3 className="font-display text-xl font-semibold leading-tight text-white xl:text-2xl">{point.title}</h3>
      </div>
      <p className="max-w-[590px] text-sm leading-[1.85] text-white/70 xl:text-base">{point.body}</p>
    </motion.article>
  );
}

export default function HeroAboutScene({ progress, stageRef }) {
  const [active, setActive] = useState(() => chapterFor(progress.get()));
  useMotionValueEvent(progress, "change", value => setActive(chapterFor(value)));
  const portraitOpacity = useTransform(progress, [0.14, 0.28, 1], [0, 1, 1]);
  const portraitScale = useTransform(progress, [0.14, 0.4, 1], [1.1, 1, 1.035]);
  const portraitX = useTransform(progress, [0.14, 0.34], [90, 0]);
  const copyOpacity = useTransform(progress, [0.265, 0.32], [0, 1]);
  const copyY = useTransform(progress, [0.265, 0.34], [38, 0]);
  const wordX = useTransform(progress, [0.26, 1], [65, -80]);
  const rail = useTransform(progress, [0.3, 0.98], [0, 1]);
  const jumpTo = index => {
    const stage = stageRef.current;
    if (!stage) return;
    const top = stage.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: top + (stage.offsetHeight - window.innerHeight) * CHAPTER_STOPS[index], behavior: "instant" });
  };

  return (
    <motion.section
      aria-labelledby="about-scene-heading"
      aria-hidden={active < 0}
      inert={active < 0}
      data-testid="about-scroll-scene"
      className="absolute inset-0 z-0 overflow-hidden bg-[#08090b]"
      style={{ opacity: portraitOpacity, pointerEvents: active < 0 ? "none" : "auto" }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgb(var(--brand-500)/0.16),transparent_55%)]"
        style={{ maskImage: "linear-gradient(to bottom, black 0%, black 55%, transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 55%, transparent 100%)" }}
      />
      <motion.div className="absolute right-[6vw] top-[max(5rem,10vh)] z-10 h-[80vh] w-[min(40vw,620px)] overflow-hidden rounded-3xl border border-white/10" style={{ scale: portraitScale, x: portraitX }}>
        <img src="/tanishq.png" alt="Tanishq Saini" className="h-full w-full object-cover object-top grayscale" />
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
        <div className="absolute bottom-6 left-6 right-6 font-mono">
          <p className="text-sm font-medium text-white">Tanishq Saini</p>
          <p className="mt-1 text-xs leading-relaxed text-white/65">BCA @ Christ University · 3.7/4 GPA · Jun 2027</p>
        </div>
      </motion.div>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden">
        <motion.span className="portfolio-backdrop-word relative top-[7vh]" style={{ x: wordX }}>About</motion.span>
      </div>
      <motion.div className="absolute left-[6vw] top-[max(6rem,12vh)] z-20 w-[43vw] max-w-[660px]" style={{ opacity: copyOpacity, y: copyY }}>
        <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-brand-400"><span className="text-white/35">01</span><span aria-hidden="true" className="h-px w-8 bg-brand-500/60" />About</p>
        <h2 id="about-scene-heading" className="mt-6 font-display text-[clamp(2.2rem,3.5vw,4.5rem)] font-bold leading-[1.06] tracking-[-0.045em] text-white">I design and engineer software that feels <span className="text-brand-400">effortless</span>.</h2>
        <div className="relative mt-8 min-h-[200px] xl:mt-10 xl:min-h-[220px]">
          {ABOUT_STORY.map((point, index) => <AboutChapter key={point.n} point={point} index={index} progress={progress} active={active === index} />)}
        </div>
        <div className="mt-4 flex items-center gap-5">
          <nav aria-label="About story" className="flex gap-1">
            {ABOUT_STORY.map((point, index) => <button key={point.n} type="button" aria-label={`Read ${point.title}`} aria-current={active === index ? "step" : undefined} onClick={() => jumpTo(index)} className={`focus-ring flex h-11 w-11 items-center justify-center rounded-full border font-mono text-xs transition-colors ${active === index ? "border-brand-500/50 bg-brand-500/15 text-brand-300" : "border-white/10 text-white/40 hover:border-white/30 hover:text-white"}`}>{point.n}</button>)}
          </nav>
          <div aria-hidden="true" className="h-px max-w-[160px] flex-1 overflow-hidden bg-white/10"><motion.div className="h-full origin-left bg-brand-400" style={{ scaleX: rail }} /></div>
        </div>
      </motion.div>
      <SceneNextLink href="#skills" label="Skills" className="z-20" />
    </motion.section>
  );
}
